/**
 * 实时行情 WebSocket 客户端（H5 / App 通用）
 *
 * - 连接后端 /api/v1/quotes/ws（QuotesHub Durable Object），约 3 秒一帧差量推送
 * - 单例连接 + 订阅者模式：多个页面共用一条连接，页面卸载时取消订阅
 * - 自动重连（指数退避，最长 30s），无订阅者时自动断开
 * - 仅 H5/App 有 WebSocket；不支持的环境静默降级（页面保留原轮询数据）
 */
import { API_BASE_URL, QUOTES_POLL_MS, QUOTES_TRANSPORT, QUOTES_WS_ENABLED } from '@/config/env'
import { getQuotesTicker } from '@/api/modules/quotes'

export interface QuoteTick {
  symbol: string
  name: string
  category: string
  quoteMode: 'ticker' | 'buyback_sale'
  unit: string
  price: string
  buyback: string
  sale: string
  buybackExTax?: string
  buybackInclTax?: string
  saleExTax?: string
  saleInclTax?: string
  high: string
  low: string
  change: string
  changePct: string
  trend: 'up' | 'down' | 'flat'
  time: string
  sourceId?: string
  priceType?: string
}

type TickHandler = (ticks: QuoteTick[]) => void

let socket: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null
let eventSource: EventSource | null = null
let reconnectDelay = 2000
let manualClosed = false
const handlers = new Set<TickHandler>()

function wsUrl(): string {
  const base = API_BASE_URL.replace(/\/+$/, '')
  return `${base.replace(/^http/, 'ws')}/api/v1/quotes/ws`
}

function streamUrl(): string {
  const base = API_BASE_URL.replace(/\/+$/, '')
  return `${base}/api/v1/quotes/stream`
}

function emit(items: QuoteTick[]): void {
  if (!items.length) return
  for (const handler of handlers) handler(items)
}

function connect(): void {
  if (typeof WebSocket === 'undefined') return
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return

  manualClosed = false
  try {
    socket = new WebSocket(wsUrl())
  } catch (err) {
    console.error('[quotes-ws] connect failed', err)
    scheduleReconnect()
    return
  }

  socket.onopen = () => {
    reconnectDelay = 2000
  }

  socket.onmessage = (evt) => {
    try {
      const data = JSON.parse(String(evt.data)) as { type?: string; items?: QuoteTick[] }
      if (data.type === 'quotes' && Array.isArray(data.items) && data.items.length) {
        emit(data.items)
      }
    } catch {
      // 非 JSON 帧（如 pong）忽略
    }
  }

  socket.onclose = () => {
    socket = null
    if (!manualClosed && handlers.size) {
      if (QUOTES_TRANSPORT === 'auto' && connectSse()) return
      scheduleReconnect()
    }
  }

  socket.onerror = () => {
    // onclose 会随后触发并负责重连
  }
}

function scheduleReconnect(): void {
  if (reconnectTimer || manualClosed || !handlers.size) return
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    reconnectDelay = Math.min(reconnectDelay * 2, 30000)
    connectTransport()
  }, reconnectDelay)
}

function connectSse(): boolean {
  if (typeof EventSource === 'undefined') return false
  if (eventSource) return true
  manualClosed = false
  eventSource = new EventSource(streamUrl())
  eventSource.addEventListener('quotes', (evt) => {
    try {
      const data = JSON.parse((evt as MessageEvent).data) as { type?: string; items?: QuoteTick[] }
      if (data.type === 'quotes' && Array.isArray(data.items)) emit(data.items)
    } catch {
      // ignore malformed frame
    }
  })
  eventSource.onerror = () => {
    eventSource?.close()
    eventSource = null
    if (!manualClosed && handlers.size) scheduleReconnect()
  }
  return true
}

function connectPoll(): void {
  if (pollTimer) return
  const pull = async () => {
    try {
      const res = await getQuotesTicker()
      emit(res.items.map((item) => ({
        symbol: item.symbol,
        name: item.name,
        category: '',
        quoteMode: item.quoteMode === 'buyback_sale' ? 'buyback_sale' : 'ticker',
        unit: item.unit ?? '',
        price: item.price,
        buyback: item.buyback && item.buyback !== '--' ? item.buyback : '--',
        sale: item.sale && item.sale !== '--' ? item.sale : '--',
        buybackExTax: item.buybackExTax,
        buybackInclTax: item.buybackInclTax,
        saleExTax: item.saleExTax,
        saleInclTax: item.saleInclTax,
        high: item.high && item.high !== '--' ? item.high : '--',
        low: item.low && item.low !== '--' ? item.low : '--',
        change: item.change,
        changePct: item.changePct,
        trend: item.trend,
        time: res.meta.time,
      })))
    } catch {
      // polling is best-effort
    }
  }
  void pull()
  pollTimer = setInterval(pull, QUOTES_POLL_MS)
}

function connectTransport(): void {
  if (QUOTES_TRANSPORT === 'poll') {
    connectPoll()
    return
  }
  if (QUOTES_TRANSPORT === 'sse') {
    if (!connectSse()) connectPoll()
    return
  }
  if (QUOTES_TRANSPORT === 'ws' || QUOTES_TRANSPORT === 'auto') {
    if (QUOTES_WS_ENABLED && typeof WebSocket !== 'undefined') {
      connect()
      return
    }
    if (QUOTES_TRANSPORT === 'auto' && connectSse()) return
  }
  connectPoll()
}

function disconnect(): void {
  manualClosed = true
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (socket) {
    try {
      socket.close()
    } catch {
      // 忽略
    }
    socket = null
  }
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

/** 订阅实时行情；返回取消订阅函数（页面 onUnload/onHide 时调用） */
export function subscribeQuotes(handler: TickHandler): () => void {
  handlers.add(handler)
  connectTransport()
  return () => {
    handlers.delete(handler)
    if (!handlers.size) disconnect()
  }
}
